import {
  Box,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Link,
} from '@chakra-ui/react';
import { sectionListSx } from '../../../utils/sectionListSx';

export const SaasTermsPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize="15px"
      lineHeight="24px">
      <VStack align="start" spacing={16}>
        {/* Header */}
        <Box>
          <Heading as="h1" fontSize="40px" mb={4} fontFamily="marketingPage">
            General SaaS Terms and Conditions
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Effective Date: 2026-01-01
          </Text>
          <Text fontSize="sm" color="gray.600">
            Last Updated: 2026-01-01
          </Text>
        </Box>

        {/* Section 1 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            1. General
          </Heading>
          <Text>
            These general SaaS terms and conditions (<strong>Terms</strong>) are
            issued by Merkatus.io Oy, a company incorporated in Finland, with
            Business ID 3541433-4 (<strong>Merkatus</strong>). These Terms,
            together with the service order form (<strong>Order</strong>) as
            well as the documents referred to in these Terms and the Order,
            comprise the entire agreement (<strong>Agreement</strong>) between
            Merkatus and each legal entity customer (<strong>Customer</strong>)
            of Merkatus’ cloud-based marketing intelligence service (a
            software-as-a-service solution used primarily via an internet
            browser) and the related offerings, if any (jointly
            <strong> Service</strong>). The Service is targeted only at
            corporate clients for professional use. By signing the Order or
            first accessing the Service, Customer agrees to be bound by the
            Agreement.
          </Text>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            2. Service
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(2)}>
            <ListItem>
              <strong>Grant of Access.</strong> For the term of the Agreement
              and subject to payment of agreed fees, Merkatus grants Customer a
              limited, non- exclusive, non-transferable, revocable right to
              access and use the Service at a level specified in the Order.
              Customer is entitled to use the Service solely for marketing
              automation, planning and decision- making purposes within
              Customer’s organization, as may be further described in the
              Service description.
            </ListItem>
            <ListItem>
              <strong>Permitted Users.</strong> The Service may only be accessed
              and used by Customer’s authorised personnel (
              <strong>Users</strong>) up to the number of user seats specified
              in the Order. Each User must accept the terms of use (
              <strong>User Terms</strong>) and Privacy Notice via
              click-acceptance before accessing the Service. Customer is
              responsible for all acts and omissions of its Users and shall
              ensure all Users comply with the applicable law and User Terms.
            </ListItem>
            <ListItem>
              <strong>Compliance.</strong> Customer commits to use the Service
              only in accordance with the applicable law and the Agreement.
              Merkatus is not liable for any loss or damage arising from
              Customer’s or its User’s breach of the Agreement or violation of
              applicable law.
            </ListItem>
            <ListItem>
              <strong>Technical Requirements.</strong> Customer is responsible
              for its own operational environment (hardware and software),
              security, required network connections, and the interoperability
              of its other systems with the Service.
            </ListItem>
            <ListItem>
              <strong>Nature of Service.</strong> The Service is a
              decision-support tool designed to assist with measuring marketing
              effectiveness and enhancing marketing-related decision-making.
              Customer acknowledges that use of the Service and all business
              decisions based on the Service are made at Customer’s own
              discretion and risk, and Merkatus makes no guarantee regarding
              business outcomes or results achieved through use of the Service.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            3. Fees
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(3)}>
            <ListItem>
              <strong>Payment Term.</strong> Customer shall pay the fees
              specified in the Order. Payment term is 14 days net. Late payments
              shall bear penalty interest from the due date at the rate set out
              in the Finnish Interest Act.
            </ListItem>
            <ListItem>
              <strong>Invoicing.</strong> Invoicing occurs monthly, quarterly or
              annually as selected in the Order. If not selected, invoicing
              occurs monthly. The Subscription term shall be always 6 months at
              minimum.
            </ListItem>
            <ListItem>
              <strong>Changes, Taxes.</strong> Merkatus may revise the fees upon
              30 days prior written notice. Fee changes shall apply only to
              future billing periods and shall not affect fees already invoiced
              or paid. All fees are exclusive of VAT and other applicable taxes,
              which shall be added to invoices. If any new taxes or governmental
              charges related to the Service are introduced, or existing taxes
              are increased, Merkatus may increase the fees accordingly.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            4. Intellectual Property
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(4)}>
            <ListItem>
              <strong>Title.</strong> Merkatus and its licensors are the
              exclusive holders of all rights and title of all segments of the
              Service, including the intellectual property and the functionality
              thereof. Customer shall acquire no rights other than explicitly
              described in the Agreement. Customer acknowledges that the Service
              and the underlying software contains business secrets of Merkatus
              or third parties, such as the special design of individual
              software components, structures, architecture and logics,
              programming techniques and documentation. Any copying, amending,
              translation or reverse engineering of the Service or the
              underlying software is prohibited.
            </ListItem>

            <ListItem>
              <strong>Third-Party IP.</strong> Merkatus will defend or settle
              any claim against Customer that the Service infringes intellectual
              property rights valid in Finland. Customer shall promptly notify
              Merkatus in writing of any such claim, and procure the necessary
              authorizations for Merkatus to independently litigate or settle
              the matter in its sole discretion. Merkatus will take appropriate
              measures and will pay costs and damages finally awarded against
              Customer by a competent court. When such a claim is made or
              appears likely Merkatus may, at its option, modify the Service,
              procure necessary licenses or provide a replacement. If none of
              these alternatives is reasonably available, Customer will cease
              using the Service and Merkatus will refund the fees paid by
              Customer for Service not yet provided on a pro-rated basis.
              Merkatus’ responsibility for third-party intellectual property
              infringements is limited to what has been stated in this Section.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            5. Maintenance
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(5)}>
            <ListItem>
              <strong>Availability.</strong> Merkatus uses reasonable business
              efforts to maintain the availability of the Service 24 hours a day
              and 7 days a week, except for (a) certain separately stated
              features of the Service (if any); and (b) maintenance breaks, of
              which Merkatus pursues to inform in advance. If Customer detects
              an error in the Service, Customer is encouraged to inform Merkatus
              thereof, after which Merkatus uses its reasonable business efforts
              to correct the errors without undue delay.
            </ListItem>
            <ListItem>
              <strong>Updates.</strong> Merkatus may enhance, modify and update
              (jointly, <strong>Update</strong>) the Service at its discretion
              at any time, using automatic updating technology or otherwise.
              Merkatus will use reasonable business efforts to ensure that the
              Update will not materially adversely impair the key functions of
              the Service. Merkatus has the right to temporarily suspend the use
              of the Service for reasonable maintenance and development actions.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 6 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            6. Security
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(6)}>
            <ListItem>
              <strong>General.</strong> Use of the Service requires username and
              password, which are created by each User upon registering to the
              Service. Customary information security of the Service is provided
              by Merkatus. Merkatus does not provide for e-mail or other message
              encryption or virus detection and is not responsible for risks
              inherent in transmitting information via the Internet or the
              Service. Merkatus may access Customer’s and User’s data to resolve
              problems, system errors, or Service-related problems. Customer is
              responsible for implementing and maintaining appropriate security
              measures for its operations in accordance with technological
              developments and evolving security needs.
            </ListItem>
            <ListItem>
              <strong>Suspension.</strong> Merkatus may temporarily prevent
              Customer’s or a User’s access to the Service, if (a) the
              operational environment, software, hardware or other actions of
              Customer/User pose a risk to other users or the operation of the
              Service; (b) legal or regulatory requirements mandate such action;
              (c) the Agreement or the User Terms are breached; or (d) security
              concerns, such as unauthorized access or malicious activity,
              arise. Temporary prevention of access to the Service shall not
              affect the agreed fees.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 7 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            7. Confidentiality
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(7)}>
            <ListItem>
              <strong>Confidential Information.</strong> Receiving party agrees
              that all information disclosed by or obtained from the other
              party, which is either marked as, or should otherwise reasonably
              have been understood to be, confidential (
              <strong>Confidential Information</strong>) will be held in
              confidence, and will be used only for purposes of the agreement.
              Either party may disclose Confidential Information to its
              affiliates, employees and partners, provided that such parties
              agree to be bound by confidentiality obligations no less stringent
              than those set forth herein.
            </ListItem>
            <ListItem>
              <strong>Exclusions.</strong> These obligations will not apply to
              information which (a) is or becomes generally available through no
              confidentiality violation of the receiving party; (b) was lawfully
              available to the receiving party prior to its disclosure; (c)
              becomes available to the receiving party from a third party on a
              non-confidential basis; (d) is developed independently by the
              receiving party having no access to such confidential information;
              or (e) is required by law, court order or authorities to be
              disclosed.
            </ListItem>
            <ListItem>
              <strong>Anonymised Data.</strong> Merkatus may collect and use
              aggregated usage data and statistics derived from Customer’s use
              of the Service in anonymised form, provided that such data cannot
              be used to identify Customer, User or any individual. Merkatus may
              use such anonymised data for product development, benchmarking,
              analytics and research purposes and may share such data with
              third-party service providers for these purposes.
            </ListItem>
            <ListItem>
              <strong>Duration.</strong> The Parties’ confidentiality
              obligations under this Section will survive termination of the
              Agreement for a period of 5 years; provided, however, that a
              party’s trade secrets will remain confidential indefinitely.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 8 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            8. Personal Data
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(8)}>
            <ListItem>
              <strong>General.</strong> Each party complies with the applicable
              data protection laws and regulations. For User account and
              authentication data, Merkatus is the data controller and processes
              such data in accordance with its Privacy Notice (
              <Link
                href="https://merkatus.io/privacy-policy"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/privacy-policy
              </Link>
              ), as amended from time to time. For personal data that Customer
              submits to the Service in the course of using the Service (
              <strong>Customer Personal Data</strong>), Customer is the data
              controller and Merkatus is the data processor. The parties’
              obligations regarding the processing of Customer Personal Data are
              set out in the Data Processing Agreement (
              <Link
                href="https://merkatus.io/dpa"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/dpa
              </Link>
              ), which forms an integral part of the Agreement.
            </ListItem>
            <ListItem>
              <strong>Customer Warranties.</strong> Customer is responsible for
              ensuring that all data uploaded to the Service by Customer or its
              Users: (a) is uploaded lawfully and in compliance with applicable
              laws; (b) does not infringe any third-party rights; and (c)
              complies with the Agreement. Customer warrants that it has all
              necessary rights, consents and lawful bases to provide such data
              to Merkatus for processing in accordance with the Agreement.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 9 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            9. Term
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(9)}>
            <ListItem>
              <strong>Commencement.</strong> The Agreement term (
              <strong>Subscription</strong>) shall commence on the date
              specified in the Order. If no date is specified, the Subscription
              shall commence on the earlier of: (a) the date Customer accesses
              the Service; or (b) the date Customer signs the Order.
            </ListItem>
            <ListItem>
              <strong>Renewal.</strong> Unless otherwise agreed in the Order,
              Subscription renews automatically for successive periods equal in
              duration to the initial Subscription period specified in the
              Order. A party may terminate for convenience by giving written
              notice during the then-current Subscription period, with such
              termination taking effect at the end of the then-current
              Subscription period.
            </ListItem>
            <ListItem>
              <strong>Termination for Breach or Insolvency.</strong> Either
              party is entitled to terminate the Agreement with immediate effect
              in the following situations: (a) for cause if the other party is
              in material breach of the Agreement and does not cure (if curable)
              the same within 14 days after having received the other party’s
              notice thereof; and (b) for convenience if the other party becomes
              insolvent, bankrupt, enters into liquidation, administration or
              receivership, or commences corporate restructuring proceedings and
              fails to provide sufficient security for its obligations under the
              Agreement.
            </ListItem>
            <ListItem>
              <strong>Consequences of Termination.</strong> Termination does not
              affect Merkatus’ right to payment for fees accrued up to and
              including the effective date of termination. Upon termination, all
              unpaid fees shall become immediately due and payable within 14
              days of the effective date of termination. No refunds will be
              issued for any fees already paid, even if termination occurs
              before the end of Subscription period. For example, if Customer
              has paid for a full year and terminates mid-period, no portion of
              those prepaid fees will be refunded. Customer’s access to the
              Service ceases upon the effective date of termination.
            </ListItem>
            <ListItem>
              <strong>Return of Customer Data.</strong> Upon Customer’s request
              made within 90 days of termination, Merkatus shall make available
              a copy of Customer’s data stored in the Service in accordance with
              Merkatus’ standard export process and in a generally used format.
            </ListItem>
            <ListItem>
              <strong>Data Act.</strong> If the Service is determined to be
              subject to the requirements of Regulation (EU) 2023/2854 (
              <strong>Data Act</strong>), the following provisions shall apply
              notwithstanding any other terms of the Agreement: (a) termination
              rights and procedures shall be governed by the Terms for Exchange
              of Data Processing Services and Data Transfer (
              <Link
                href="https://merkatus.io/terms-of-exchange"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/terms-of-exchange
              </Link>
              ); and (b) Customer may terminate the Agreement in accordance with
              the Data Act’s mandatory switching provisions, even if committed
              to a longer contract period. In the event of early termination
              pursuant to the Data Act, Customer shall pay Merkatus an early
              termination fee calculated as follows: (a) for the first 2 months
              of the remaining Subscription period, 100% of the monthly
              Subscription fees; and (b) for any period exceeding 2 months of
              the remaining Subscription period: 75% of the monthly Subscription
              fees for such excess period. The parties acknowledge that this
              amount represents Merkatus’ reasonable costs associated with early
              termination and constitutes fair compensation for premature
              contract termination in accordance with Data Act.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 10 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            10. Limitation of Liability
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(10)}>
            <ListItem>
              <strong>No Warranties.</strong> To the extent allowed by mandatory
              law, Merkatus provides the Service and its other offerings
              hereunder “as is” and “as available” without warranties of any
              kind, including that the Service would be error-free or fit for a
              particular purpose.
            </ListItem>
            <ListItem>
              <strong>Limitation of Liability.</strong> Merkatus is not liable
              for any indirect, or consequential damages such as loss of data or
              profits. The total aggregate liability of Merkatus is limited in
              all circumstances to a sum corresponding to 3 months’ VAT-free
              Service fees paid by Customer to Merkatus. The limitations of
              liability agreed hereunder will not apply to the extent caused by
              gross negligence or wilful misconduct, or breach of
              confidentiality obligations.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 11 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            11. Miscellaneous
          </Heading>
          <UnorderedList pl={4} spacing={2} sx={sectionListSx(11)}>
            <ListItem>
              <strong>Applicable Law, Disputes.</strong> The Agreement shall be
              governed by the laws of Finland, excluding its choice of law
              provisions. The parties will aim to resolve their possible
              disagreements primarily through negotiation. If the negotiations
              do not produce results within a reasonable time, any dispute,
              controversy or claim arising out of or relating to the Agreement,
              will be settled by the District Court of Helsinki as the first
              instance.
            </ListItem>
            <ListItem>
              <strong>Survival.</strong> Termination of the Agreement will not
              affect any liabilities or rights accruing prior to the effective
              date of such termination, or that are of a continuing nature. Any
              terms and conditions that by their nature or their explicit
              wording should survive a cancellation or termination of the
              Agreement shall also survive.
            </ListItem>
            <ListItem>
              <strong>Reference.</strong> Either party may disclose publicly
              that the parties have entered into the Agreement and that the
              other party is a customer or vendor of the other party.
            </ListItem>
            <ListItem>
              <strong>Force Majeure.</strong> Except for Customer’s payment
              obligations hereunder, neither party will be liable for any
              failure or delay in performing its obligations to the extent such
              failure or delay is caused by events beyond its reasonable
              control, including but not limited to, war, civil unrest, labour
              disputes, failure of suppliers or subcontractors, fire, flood,
              pandemic, governmental actions, or failure of telecommunications
              or internet services.
            </ListItem>
            <ListItem>
              <strong>Backups.</strong> Merkatus will perform automated backups
              of Customer data in the Service at least weekly and retain such
              backups for a minimum 90 days. Customer may request restoration of
              data from backups by submitting a support request through the
              Service. Merkatus will use commercially reasonable efforts to
              restore data as soon as possible [but does not guarantee
              successful recovery in all cases].
            </ListItem>
            <ListItem>
              <strong>Assignment, Subcontractors.</strong> Neither party may
              assign the Agreement either part or in whole without prior written
              consent of the other party. Merkatus is entitled to assign its
              receivables to a third party. A party is entitled to use
              subcontractors but is responsible for its subcontractor’s acts and
              omissions as for its own.
            </ListItem>
            <ListItem>
              <strong>Severability, No Waiver.</strong> If any provision of the
              Agreement is held by a court of competent jurisdiction to be
              invalid, illegal, or unenforceable, then the remainder of the
              Agreement will remain in full force and effect. No delay or
              omission by either party to exercise any right or power it has
              under the Agreement will impair or be construed as a waiver of
              such right or power.
            </ListItem>
            <ListItem>
              <strong>Modification.</strong> Merkatus may amend these Terms, the
              Service Description (
              <Link
                href="https://merkatus.io/service-description"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/service-description
              </Link>
              ), Terms for Exchange of Data Processing Services (
              <Link
                href="https://merkatus.io/terms-of-exchange"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/terms-of-exchange
              </Link>
              ) and Data Transfer (
              <Link
                href="https://merkatus.io/terms-of-exchange"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/terms-of-exchange
              </Link>
              ) and User Terms (
              <Link
                href="https://merkatus.io/user-terms"
                color="darkBlue.900"
                isExternal>
                https://merkatus.io/user-terms
              </Link>
              ) from time to time, upon 30 days prior written notice. If
              Customer does not agree to the amendment, Customer may terminate
              the Agreement by providing written notice to Merkatus within 15
              days of receiving notice of the amendment, with termination
              effective on the date the amendment would otherwise take effect.
              If Merkatus withdraws the proposed amendment before its effective
              date, Customer’s termination notice shall be void and the
              Agreement shall continue under the existing terms.
            </ListItem>
          </UnorderedList>
        </Box>

        {/* Section 12 */}
        <Box>
          <Heading as="h2" fontSize="32px" mb={4} fontFamily="marketingPage">
            12. Contact
          </Heading>
          <Text>
            Merkatus.io Oy
            <br />
            Lapinlahdenkatu 16, 00180 Helsinki, Finland
            <br />
            <Link
              href="mailto:support@merkatus.io"
              color="darkBlue.900"
              fontWeight="semibold">
              support@merkatus.io
            </Link>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
